import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/projects", label: "Projects" },
  { to: "/about", label: "About" },
  { to: "/process", label: "Process" },
  { to: "/services", label: "Services" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-zinc-950/80 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center group"
        >
          <img
            src="/tidemark-logo.png"
            alt="Tidemark Building Co."
            className="h-6 sm:h-7 w-auto object-contain select-none"
            draggable={false}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[13px] tracking-[0.18em] uppercase transition-colors ${
                  isActive
                    ? "text-zinc-100"
                    : "text-zinc-500 hover:text-zinc-100"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-enquire"
            className="ml-4 inline-flex items-center gap-2 px-5 py-2.5 border border-zinc-700 hover:border-zinc-100 text-zinc-100 text-[12px] tracking-[0.2em] uppercase transition-all duration-500 hover:bg-zinc-100 hover:text-zinc-950"
          >
            Enquire
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-zinc-100 p-2"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="lg:hidden bg-zinc-950/95 backdrop-blur-xl border-t border-white/5"
        >
          <div className="px-6 py-8 flex flex-col gap-6">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-serif text-2xl ${
                    isActive ? "text-zinc-100" : "text-zinc-500"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-mobile-cta"
              className="mt-4 inline-flex items-center justify-center px-5 py-3 border border-zinc-700 text-zinc-100 text-[12px] tracking-[0.2em] uppercase"
            >
              Enquire
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
